// BILLING-PLANS (§6.1): the company's invoices, newest first, cursor-paginated — number, what,
// period or date, due, total, status — with Pay ↗ (Stripe's hosted page: card, ACH or bank
// transfer; owners and admins, on Open and Overdue) and PDF ↗ on hover.
import {
  EmptyState,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CreditCardIcon, FileDownIcon, ReceiptIcon } from "lucide-react";
import type { Api } from "../../../api/client";
import { formatDate, formatMoney } from "../../format";
import { LoadError, Loading, LoadMore } from "../../screen-parts";
import { DENSE_TABLE, RowActions } from "../../settings/row-actions";
import { billingInvoicesQuery, type InvoiceView } from "../plan-queries";
import { InvoiceStatusChip, invoiceWhen, RowLinkOut, SectionHead, SOURCE_LABEL } from "./parts";

export function InvoicesTable({
  api,
  companyId,
  canPay,
  hideWhenEmpty = false,
}: {
  api: Api;
  companyId: string;
  canPay: boolean;
  /** No plan yet: the section shows only once an invoice exists. */
  hideWhenEmpty?: boolean;
}) {
  const q = useInfiniteQuery(billingInvoicesQuery(api, companyId));
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  if (hideWhenEmpty && (q.isPending || (q.isSuccess && rows.length === 0))) return null;
  return (
    <section aria-labelledby="billing-invoices" className="flex flex-col gap-2">
      <SectionHead id="billing-invoices" title="Invoices" />
      {q.isPending ? (
        <Loading label="Loading invoices" rows={2} />
      ) : q.isError ? (
        <LoadError what="Invoices" error={q.error} />
      ) : rows.length === 0 ? (
        <EmptyState variant="compact" align="start" icon={ReceiptIcon} title="No invoices yet" />
      ) : (
        <Table data-slot="billing-invoices" className={DENSE_TABLE}>
          <TableCaption className="sr-only">The company's invoices, newest first.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Number</TableHead>
              <TableHead scope="col">What</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Period or date
              </TableHead>
              <TableHead scope="col">Due</TableHead>
              <TableHead scope="col" className="text-right">
                Total
              </TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} canPay={canPay} />
            ))}
          </TableBody>
        </Table>
      )}
      <LoadMore
        what="invoices"
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
    </section>
  );
}

function InvoiceRow({ inv, canPay }: { inv: InvoiceView; canPay: boolean }) {
  const name = inv.number ?? "draft invoice";
  const payable =
    canPay &&
    inv.hosted_url !== null &&
    (inv.display_status === "open" || inv.display_status === "overdue");
  return (
    <TableRow className="group/row" data-invoice={inv.number ?? inv.id}>
      <TableHead scope="row" className="font-medium tabular-nums">
        {inv.number ?? "—"}
      </TableHead>
      <TableCell>{SOURCE_LABEL[inv.source]}</TableCell>
      <TableCell className="text-muted-foreground max-md:hidden">{invoiceWhen(inv)}</TableCell>
      <TableCell className="text-muted-foreground">
        {inv.due_date ? formatDate(inv.due_date) : "—"}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatMoney(inv.total_minor, inv.currency)}
      </TableCell>
      <TableCell>
        <InvoiceStatusChip invoice={inv} />
      </TableCell>
      <TableCell className="w-px">
        <RowActions>
          {payable && inv.hosted_url ? (
            <RowLinkOut
              icon={CreditCardIcon}
              label={`Pay ${name}`}
              tooltip="Pay ↗"
              href={inv.hosted_url}
            />
          ) : null}
          {inv.pdf_url ? (
            <RowLinkOut
              icon={FileDownIcon}
              label={`PDF of ${name}`}
              tooltip="PDF ↗"
              href={inv.pdf_url}
            />
          ) : null}
        </RowActions>
      </TableCell>
    </TableRow>
  );
}
